import { useState, useEffect } from 'react';
import { MapPin, Plus, ChevronDown } from 'lucide-react';
import { useEmbed, Property } from '../contexts/EmbedContext';

interface PropertySelectorProps {
  selectedPropertyId?: string;
  onPropertySelect: (propertyId: string | undefined) => void;
  className?: string;
}

export function PropertySelector({
  selectedPropertyId,
  onPropertySelect,
  className = '',
}: PropertySelectorProps) {
  const { userContext, requestUserContext, config } = useEmbed();
  const [isOpen, setIsOpen] = useState(false);
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (config.isEmbed && !userContext) {
      loadUserContext();
    }
  }, [config.isEmbed]);

  useEffect(() => {
    if (userContext?.properties) {
      setProperties(userContext.properties);
    }
  }, [userContext]);

  const loadUserContext = async () => {
    setLoading(true);
    try {
      await requestUserContext();
    } catch (error) {
      console.error('Failed to load user context:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectedProperty = properties.find((p) => p.id === selectedPropertyId);

  if (!config.isEmbed) {
    return null;
  }

  if (loading) {
    return (
      <div className={`animate-pulse ${className}`}>
        <div className="h-10 bg-gray-200 rounded-lg"></div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Associate with Property
      </label>

      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between px-4 py-2.5 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-900 transition-colors duration-200"
        >
          <div className="flex items-center">
            <MapPin className="h-5 w-5 text-gray-400 mr-2" />
            <span className={selectedProperty ? 'text-gray-900' : 'text-gray-500'}>
              {selectedProperty ? selectedProperty.address : 'Select a property...'}
            </span>
          </div>
          <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => setIsOpen(false)}
            />
            <div className="absolute z-20 w-full mt-2 bg-white rounded-lg shadow-lg border border-gray-200 max-h-64 overflow-y-auto">
              <div className="py-1">
                <button
                  onClick={() => {
                    onPropertySelect(undefined);
                    setIsOpen(false);
                  }}
                  className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150"
                >
                  <span className="text-gray-500">No property selected</span>
                </button>

                {properties.length === 0 ? (
                  <div className="px-4 py-6 text-center text-sm text-gray-500">
                    <MapPin className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    <p>No properties found</p>
                    <p className="text-xs mt-1">Add properties in your account settings</p>
                  </div>
                ) : (
                  properties.map((property) => (
                    <button
                      key={property.id}
                      onClick={() => {
                        onPropertySelect(property.id);
                        setIsOpen(false);
                      }}
                      className={`w-full text-left px-4 py-3 text-sm hover:bg-gray-50 transition-colors duration-150 ${
                        property.id === selectedPropertyId ? 'bg-gray-100' : ''
                      }`}
                    >
                      <div className="flex items-start">
                        <MapPin className={`h-4 w-4 mt-0.5 mr-2 flex-shrink-0 ${
                          property.id === selectedPropertyId ? 'text-gray-900' : 'text-gray-400'
                        }`} />
                        <div className="flex-1">
                          <div className={`font-medium ${
                            property.id === selectedPropertyId ? 'text-gray-900' : 'text-gray-900'
                          }`}>
                            {property.address}
                          </div>
                          {property.propertyType && (
                            <div className="text-xs text-gray-500 mt-0.5 capitalize">
                              {property.propertyType}
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <p className="mt-1.5 text-xs text-gray-500">
        Link this document to a specific property for better organization
      </p>
    </div>
  );
}
